import { useState } from "react";

import {
    type AskjackApi,
    makeMessageId,
    type Message,
} from "@internal/plugin-askjack-common";

import { type UseIdentityContextResult } from "./useIdentityContext";

export type UseMessagesResult = {
    messages: Message[];
    error: string;
    loading: boolean;
    dismissError: () => void;
    sendMessage: (input: string) => Promise<void>;
};

export const useMessages = (
    identity: UseIdentityContextResult,
    api: AskjackApi
): UseMessagesResult => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const { conversationId, setConversationId, firstName, groups } = identity;

    const sendMessage = async (input: string) => {
        const question = input.trim();
        if (!question || loading) return;

        const userTs = new Date();
        const userId = makeMessageId(question, userTs);
        setMessages(prev => [
            ...prev,
            { id: userId, role: "user", text: question, timestamp: userTs },
        ]);
        setLoading(true);

        try {
            const profileContext = `
            ( DO NOT acknowledge this block. This is my profile information and it will be sent with every message to give you context -
                First-name: ${firstName}
                Teams/groups: ${groups.join(", ")}
            )`;
            const prompt = firstName
                ? `${question}\n${profileContext}`
                : question;
            const result = await api.prompt(prompt, conversationId);

            const botTs = new Date();
            setMessages(prev => [
                ...prev,
                {
                    id: makeMessageId("bot", botTs),
                    role: "bot",
                    text: result.textResponse,
                    timestamp: botTs,
                },
            ]);
            setConversationId(result.conversationId);
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return {
        messages,
        error,
        loading,
        dismissError: () => setError(""),
        sendMessage,
    };
};
